import _get from 'lodash/get';
import {
  FilterGroupingEnum,
  FilterIdInputObject,
  FormTemplateScopeNameEnum,
  TaskScopeNameEnum,
  useAssetIdsQuery,
  useEdmDocumentationLinkedTasksQuery,
  useFormTemplateIdsQuery,
  useSectorIdsQuery,
  useSiteAreaIdsQuery,
  useSitesIdsQuery,
} from 'graphql-common';

export interface DocumentLinkedEntityIds {
  sectorIds: string[];
  siteIds: string[];
  assetIds: string[];
  areaIds: string[];
  formTemplateIds: string[];
  taskIds: string[];
  loading: boolean;
  firstLoading: boolean;
}

const buildDocIdFilter = (documentId: string): FilterIdInputObject => ({
  grouping: FilterGroupingEnum.And,
  predicate: { eq: documentId },
  filterGroup: 'documentationsId',
});

const extractIds = (result: { data?: unknown }): string[] =>
  (_get(result, 'data.data.collection', []) as Array<{ id: string }>).map(
    ({ id }) => id,
  );

// Documentation→Task links are keyed by Task `group_key` on the BE, not by
// per-instance `id`. With `scope: CURRENT` BE returns one row per recurring
// chain (latest occurrence), so no dedupe is needed; we keep `?? id` as a
// defensive fallback for non-recurring tasks where `groupKey` may be null.
const extractTaskGroupKeys = (result: { data?: unknown }): string[] =>
  (
    _get(result, 'data.data.collection', []) as Array<{
      id: string;
      groupKey?: string | null;
    }>
  ).map(({ id, groupKey }) => groupKey ?? id);

export default function useDocumentLinkedEntityIds({
  documentId,
  isPlanLocked,
  skip,
}: {
  documentId: string;
  /**
   * Free plan: BE keeps `documentationsId` filter gated on
   * sectors / sites / siteAreas / formTemplates / tasks, but un-gated it
   * on `assets`. Skip the gated five and let the asset query through so
   * the doc-edit form can still prefill the linked equipment.
   */
  isPlanLocked?: boolean;
  skip?: boolean;
}): DocumentLinkedEntityIds {
  const isSkipped = skip || !documentId;
  const isPremiumSkipped = isSkipped || !!isPlanLocked;
  const docIdFilter = buildDocIdFilter(documentId);

  const sectorsResult = useSectorIdsQuery({
    fetchPolicy: 'cache-and-network',
    skip: isPremiumSkipped,
    variables: {
      filters: [{ documentationsId: docIdFilter }],
    },
  });

  const sitesResult = useSitesIdsQuery({
    fetchPolicy: 'cache-and-network',
    skip: isPremiumSkipped,
    variables: {
      onlyIds: true,
      filters: [{ documentationsId: docIdFilter }],
    },
  });

  const assetsResult = useAssetIdsQuery({
    fetchPolicy: 'cache-and-network',
    skip: isSkipped,
    variables: {
      onlyIds: true,
      filters: [{ documentationsId: docIdFilter }],
    },
  });

  const areasResult = useSiteAreaIdsQuery({
    fetchPolicy: 'cache-and-network',
    skip: isPremiumSkipped,
    variables: {
      onlyIds: true,
      filters: [{ documentationsId: docIdFilter }],
    },
  });

  const formTemplatesResult = useFormTemplateIdsQuery({
    fetchPolicy: 'cache-and-network',
    skip: isPremiumSkipped,
    variables: {
      onlyIds: true,
      scopeName: FormTemplateScopeNameEnum.Active,
      filters: [{ documentationsId: docIdFilter }],
    },
  });

  const tasksResult = useEdmDocumentationLinkedTasksQuery({
    fetchPolicy: 'cache-and-network',
    skip: isPremiumSkipped,
    variables: {
      scope: { name: TaskScopeNameEnum.Current },
      filters: [{ documentationsId: docIdFilter }],
    },
  });

  const results = [
    sectorsResult,
    sitesResult,
    assetsResult,
    areasResult,
    formTemplatesResult,
    tasksResult,
  ];

  const loading = results.some((r) => r.loading);
  const firstLoading =
    !isSkipped &&
    results.some((r) => r.loading && r.previousData === undefined);

  return {
    sectorIds: extractIds(sectorsResult),
    siteIds: extractIds(sitesResult),
    assetIds: extractIds(assetsResult),
    areaIds: extractIds(areasResult),
    formTemplateIds: extractIds(formTemplatesResult),
    taskIds: extractTaskGroupKeys(tasksResult),
    loading,
    firstLoading,
  };
}
